import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@uigy/shared/services/language.service';
import {
  EventCategoryType,
  ExprOp,
  GroupedAssetExpressionsParams,
  GroupedAssetsField,
  GroupedEventsExpressionsParams,
  GroupedPolicyHitsField,
  IcpPolicyHitField,
  IemRecentEventsExpressionsParams,
  PluginSeverity,
  TimeFrame,
} from '@uigy/generated/graphql';
import { KpiSmallVariant, WhatsNewKpiConfig } from '@uigy/modules/overview/kpis/kpi.interfaces';
import { KpiWhatsNewService } from '@uigy/modules/overview/kpis/kpi-whats-new/services/kpi-whats-new.service';
import { TimeFrameService } from '@uigy/shared/services/time-frame.service';

@Injectable({
  providedIn: 'root'
})
export class KpiWhatsNewEMConfigService {
  private languageService = inject(LanguageService);
  private kpiWhatsNewService = inject(KpiWhatsNewService);
  private timeFrameService = inject(TimeFrameService);

  private readonly texts = this.languageService.texts.overview.kpi_whats_new_widgets.em;

  private readonly AssetsDiscovered: WhatsNewKpiConfig<GroupedAssetsField> = {
    timeFrameValue: this.timeFrameService.getTimeFrameOption(TimeFrame.OneDay),
    filter: timeFrameValue => ({
      field: GroupedAssetsField.FirstSeen,
      op: ExprOp.Greater,
      values: this.timeFrameService.timeFrameToDateTime(timeFrameValue).toISOString()
    }),
    fetchData: filter => this.kpiWhatsNewService.getGroupedAssetCount(filter as GroupedAssetExpressionsParams),
    title: this.texts.assets_discovered.text,
    isClickable: false,
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly EventsOneDay: WhatsNewKpiConfig<GroupedPolicyHitsField> = {
    timeFrameValue: this.timeFrameService.getTimeFrameOption(TimeFrame.OneDay),
    filter: timeFrameValue => ({
      field: GroupedPolicyHitsField.EventTime,
      op: ExprOp.GreaterEqual,
      values: this.timeFrameService.timeFrameToDateTime(timeFrameValue).toISOString()
    }),
    fetchData: filter => this.kpiWhatsNewService.getGroupedEventCount(filter as GroupedEventsExpressionsParams),
    title: this.texts.events_one_day.text,
    isClickable: false,
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly RiskEvents: WhatsNewKpiConfig<GroupedPolicyHitsField> = {
    timeFrameValue: this.timeFrameService.getTimeFrameOption(TimeFrame.OneDay),
    filter: timeFrameValue => ({
      op: ExprOp.And,
      expressions: [
        {
          field: GroupedPolicyHitsField.EventTime,
          op: ExprOp.Greater,
          values: this.timeFrameService.timeFrameToDateTime(timeFrameValue).toISOString()
        },
        {field: GroupedPolicyHitsField.Severity, op: ExprOp.Equal, values: PluginSeverity.High}
      ]
    }),
    fetchData: filter => this.kpiWhatsNewService.getGroupedEventCount(filter as GroupedEventsExpressionsParams),
    title: this.texts.risk_events.text,
    isClickable: false,
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly ICPS: WhatsNewKpiConfig = {
    fetchData: () => this.kpiWhatsNewService.getIcpCount(),
    title: this.texts.icps.text,
    isClickable: false,
    kpiVariant: KpiSmallVariant.Ratio,
  };

  private readonly EventsOneWeek: WhatsNewKpiConfig<GroupedPolicyHitsField> = {
    timeFrameValue: this.timeFrameService.getTimeFrameOption(TimeFrame.OneWeek),
    filter: timeFrameValue => ({
      op: ExprOp.And,
      expressions: [
        {
          field: GroupedPolicyHitsField.EventTime,
          op: ExprOp.Greater,
          values: this.timeFrameService.timeFrameToDateTime(timeFrameValue).toISOString()
        },
        {field: GroupedPolicyHitsField.Resolved, op: ExprOp.Equal, values: false}
      ]
    }),
    fetchData: filter => this.kpiWhatsNewService.getGroupedEventCount(filter as GroupedEventsExpressionsParams),
    title: this.texts.events_one_week.text,
    isClickable: false,
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly EventsOneHour: WhatsNewKpiConfig<IcpPolicyHitField> = {
    timeFrameValue: this.timeFrameService.getTimeFrameOption(TimeFrame.OneHour),
    filter: timeFrameValue => ({
      field: IcpPolicyHitField.Time,
      op: ExprOp.GreaterEqual,
      values: this.timeFrameService.timeFrameToDateTime(timeFrameValue).toISOString()
    }),
    fetchData: filter => this.kpiWhatsNewService.getRecentEventCount(filter as IemRecentEventsExpressionsParams),
    title: this.texts.events_one_hour.text,
    isClickable: false,
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly ConfigurationEvents: WhatsNewKpiConfig<GroupedPolicyHitsField> = {
    timeFrameValue: this.timeFrameService.getTimeFrameOption(TimeFrame.OneDay),
    filter: timeFrameValue => ({
      op: ExprOp.And,
      expressions: [
        {
          field: GroupedPolicyHitsField.EventTime,
          op: ExprOp.Greater,
          values: this.timeFrameService.timeFrameToDateTime(timeFrameValue).toISOString()
        },
        {field: GroupedPolicyHitsField.Category, op: ExprOp.Equal, values: EventCategoryType.ConfigurationEvents}
      ]
    }),
    fetchData: filter => this.kpiWhatsNewService.getGroupedEventCount(filter as GroupedEventsExpressionsParams),
    title: this.texts.configuration_events.text,
    isClickable: false,
    kpiVariant: KpiSmallVariant.Basic,
  };

  private readonly Sensors: WhatsNewKpiConfig = {
    fetchData: () => this.kpiWhatsNewService.getSensorCount(),
    title: this.texts.sensors.text,
    isClickable: false,
    kpiVariant: KpiSmallVariant.Ratio,
  };

  constructor() {
  }

  public getConfigs(): WhatsNewKpiConfig[] {
    return [
      this.AssetsDiscovered,
      this.EventsOneDay,
      this.RiskEvents,
      this.ICPS,
      this.EventsOneWeek,
      this.EventsOneHour,
      this.ConfigurationEvents,
      this.Sensors,
    ];
  }
}

